import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import api from './api'

/**
 * Upload Service
 *
 * Serviço para upload de arquivos (avatares, thumbnails, etc)
 *
 * IMPORTANTE: Este serviço usa o script PHP provisório para upload.
 * Em produção, migre para o backend Node.js/Fastify.
 */

interface UploadResponse {
  success: boolean
  url: string
  filename: string
  size: number
  message: string
}

interface UploadError {
  error: string
}

/**
 * Faz upload de um avatar para o servidor PHP
 * Requer autenticação JWT
 *
 * @param file - Arquivo de imagem (JPG, PNG, WEBP, GIF)
 * @returns URL da imagem enviada
 */
export async function uploadAvatar(file: File): Promise<string> {
  // Validação client-side
  const maxSize = 5 * 1024 * 1024 // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use JPG, PNG, WEBP ou GIF')
  }

  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  // Obtém o token JWT do localStorage
  const token = localStorage.getItem('token')

  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload de avatar')
  }

  // Prepara FormData
  const formData = new FormData()
  formData.append('avatar', file)

  try {
    // Endpoint PHP (ajuste a URL conforme necessário)
    const uploadUrl = import.meta.env.VITE_UPLOAD_URL || '/upload-avatar.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`, // Envia token JWT
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }

    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data as UploadError
      throw new Error(errorData.error || 'Erro ao fazer upload do arquivo')
    }
    throw error
  }
}

/**
 * Atualiza o avatar do usuário no backend
 * @param avatarUrl - Nova URL do avatar
 * @param oldAvatarUrl - URL antiga do avatar (opcional) para deletar
 */
async function updateUserAvatar(avatarUrl: string, oldAvatarUrl?: string): Promise<void> {
  await api.put('/users/profile', {
    avatar: avatarUrl,
    oldAvatar: oldAvatarUrl, // Envia a URL antiga para o backend deletar
  })
}

/**
 * Faz upload de uma thumbnail para o servidor PHP
 * Requer autenticação JWT e permissão de admin
 * A imagem será redimensionada para 1024x1024px
 *
 * @param file - Arquivo de imagem (JPG, PNG, WEBP)
 * @returns URL da thumbnail enviada
 */
export async function uploadThumbnail(file: File): Promise<string> {
  // Validação client-side
  const maxSize = 10 * 1024 * 1024 // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use JPG, PNG ou WEBP')
  }

  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 10MB')
  }

  // Obtém o token JWT do localStorage
  const token = localStorage.getItem('token')

  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload de thumbnail')
  }

  // Prepara FormData
  const formData = new FormData()
  formData.append('thumbnail', file)

  try {
    // Endpoint PHP para thumbnails
    const uploadUrl = import.meta.env.VITE_UPLOAD_THUMBNAIL_URL || '/upload-thumbnail.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }

    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data as UploadError
      throw new Error(errorData.error || 'Erro ao fazer upload da thumbnail')
    }
    throw error
  }
}

/**
 * Hook React Query para upload de avatar
 * Automaticamente:
 * 1. Faz upload da imagem no PHP
 * 2. Atualiza o perfil do usuário no backend Node.js com a URL
 * 3. Deleta a foto antiga do servidor
 * 4. Invalida cache do perfil
 *
 * @example
 * ```tsx
 * const { mutate: uploadAvatar, isPending } = useUploadAvatar()
 * const { user } = useAuth()
 *
 * const handleFileSelect = (file: File) => {
 *   uploadAvatar({ file, oldAvatarUrl: user?.avatar }, {
 *     onSuccess: () => {
 *       toast({ title: 'Avatar atualizado com sucesso!' })
 *     },
 *     onError: (error) => {
 *       toast({ title: 'Erro ao atualizar avatar', description: error.message })
 *     }
 *   })
 * }
 * ```
 */
/**
 * Hook pra upload de foto de mentor — reusa o endpoint `/upload-avatar.php`
 * (mesmo aspecto 1:1, mesmos limites) mas NÃO chama `updateUserAvatar`
 * — não estamos mudando o avatar do usuário logado, apenas armazenando
 * a URL pra salvar em `mentors.photo` via PUT/POST /admin/mentors.
 */
export function useUploadMentorPhoto() {
  return useMutation({
    mutationFn: async (file: File) => {
      const photoUrl = await uploadAvatar(file)
      return photoUrl
    },
  })
}

export function useUploadAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ file, oldAvatarUrl }: { file: File; oldAvatarUrl?: string }) => {
      // 1. Upload da imagem no PHP
      const avatarUrl = await uploadAvatar(file)

      // 2. Atualiza o perfil no backend Node.js e deleta a foto antiga
      await updateUserAvatar(avatarUrl, oldAvatarUrl)

      return avatarUrl
    },
    onSuccess: () => {
      // 3. Invalida cache do perfil para recarregar com novo avatar
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
    },
  })
}

/**
 * Hook React Query para upload de thumbnail de episódio
 * Apenas admins podem fazer upload
 * A imagem será automaticamente redimensionada para 1024x1024px
 *
 * @example
 * ```tsx
 * const { mutate: uploadThumbnail, isPending } = useUploadThumbnail()
 *
 * const handleFileSelect = (file: File) => {
 *   uploadThumbnail(file, {
 *     onSuccess: (url) => {
 *       // Atualiza o campo thumbnail do episódio com a URL
 *       updateEpisode({ thumbnailUrl: url })
 *       toast({ title: 'Thumbnail enviada com sucesso!' })
 *     },
 *     onError: (error) => {
 *       toast({ title: 'Erro', description: error.message, variant: 'destructive' })
 *     }
 *   })
 * }
 * ```
 */
export function useUploadThumbnail() {
  return useMutation({
    mutationFn: async (file: File) => {
      // Upload da thumbnail no PHP (redimensiona para 1024x1024)
      const thumbnailUrl = await uploadThumbnail(file)
      return thumbnailUrl
    },
  })
}

/**
 * Faz upload de uma thumbnail de drop para o servidor PHP
 * Resize/crop para 9:16 (1080x1920px) — proporção de Shorts
 */
export async function uploadDropThumbnail(file: File): Promise<string> {
  const maxSize = 10 * 1024 * 1024
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use JPG, PNG ou WEBP')
  }

  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 10MB')
  }

  const token = localStorage.getItem('token')
  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload')
  }

  const formData = new FormData()
  formData.append('thumbnail', file)

  const uploadUrl = import.meta.env.VITE_UPLOAD_DROP_THUMBNAIL_URL || '/upload-drop-thumbnail.php'

  const response = await axios.post<UploadResponse>(uploadUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  })

  if (response.data.success && response.data.url) {
    return response.data.url
  }

  throw new Error(response.data.message || 'Erro ao fazer upload')
}

export function useUploadDropThumbnail() {
  return useMutation({
    mutationFn: async (file: File) => {
      return await uploadDropThumbnail(file)
    },
  })
}

/**
 * Faz upload de um workbook (PDF, DOC, XLS, etc) para o servidor PHP
 * Requer autenticação JWT e permissão de admin
 *
 * @param file - Arquivo de documento (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT)
 * @returns URL do workbook enviado
 */
export async function uploadWorkbook(file: File): Promise<string> {
  // Validação client-side
  const maxSize = 50 * 1024 * 1024 // 50MB

  // Valida tamanho
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 50MB')
  }

  // Valida extensão (mais confiável que MIME type)
  const fileName = file.name.toLowerCase()
  const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt']
  const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext))

  if (!hasValidExtension) {
    throw new Error('Tipo de arquivo não permitido. Use PDF, DOC, DOCX, XLS, XLSX, PPT ou TXT')
  }

  // Obtém o token JWT do localStorage
  const token = localStorage.getItem('token')

  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload de workbook')
  }

  // Prepara FormData
  const formData = new FormData()
  formData.append('workbook', file)

  try {
    // Endpoint PHP para workbooks
    const uploadUrl = import.meta.env.VITE_UPLOAD_WORKBOOK_URL || '/upload-workbook.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }

    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data as UploadError
      throw new Error(errorData.error || 'Erro ao fazer upload do workbook')
    }
    throw error
  }
}

/**
 * Hook React Query para upload de workbook de episódio
 * Apenas admins podem fazer upload
 * Aceita PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT (até 50MB)
 *
 * @example
 * ```tsx
 * const { mutate: uploadWorkbook, isPending } = useUploadWorkbook()
 *
 * const handleFileSelect = (file: File) => {
 *   uploadWorkbook(file, {
 *     onSuccess: (url) => {
 *       updateEpisode({ workbookUrl: url })
 *       toast({ title: 'Workbook enviado com sucesso!' })
 *     },
 *     onError: (error) => {
 *       toast({ title: 'Erro', description: error.message, variant: 'destructive' })
 *     }
 *   })
 * }
 * ```
 */
export function useUploadWorkbook() {
  return useMutation({
    mutationFn: async (file: File) => {
      // Upload do workbook no PHP
      const workbookUrl = await uploadWorkbook(file)
      return workbookUrl
    },
  })
}

/**
 * Faz upload de uma capa de workbook para o servidor PHP
 * Requer autenticação JWT e permissão de admin
 * A imagem será redimensionada para proporção 16:9 (1280x720px)
 *
 * @param file - Arquivo de imagem (JPG, PNG, WEBP)
 * @returns URL da capa enviada
 */
export async function uploadWorkbookCover(file: File): Promise<string> {
  // Validação client-side
  const maxSize = 10 * 1024 * 1024 // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use JPG, PNG ou WEBP')
  }

  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 10MB')
  }

  // Obtém o token JWT do localStorage
  const token = localStorage.getItem('token')

  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload de capa de workbook')
  }

  // Prepara FormData
  const formData = new FormData()
  formData.append('workbookCover', file)

  try {
    // Endpoint PHP para capas de workbook
    const uploadUrl = import.meta.env.VITE_UPLOAD_WORKBOOK_COVER_URL || '/upload-workbook-cover.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }

    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data as UploadError
      throw new Error(errorData.error || 'Erro ao fazer upload da capa de workbook')
    }
    throw error
  }
}

/**
 * Hook React Query para upload de capa de workbook de episódio
 * Apenas admins podem fazer upload
 * A imagem será automaticamente redimensionada para 1280x720px (16:9)
 *
 * @example
 * ```tsx
 * const { mutate: uploadWorkbookCover, isPending } = useUploadWorkbookCover()
 *
 * const handleFileSelect = (file: File) => {
 *   uploadWorkbookCover(file, {
 *     onSuccess: (url) => {
 *       updateEpisode({ workbookCoverUrl: url })
 *       toast({ title: 'Capa enviada com sucesso!' })
 *     },
 *     onError: (error) => {
 *       toast({ title: 'Erro', description: error.message, variant: 'destructive' })
 *     }
 *   })
 * }
 * ```
 */
export function useUploadWorkbookCover() {
  return useMutation({
    mutationFn: async (file: File) => {
      // Upload da capa de workbook no PHP (redimensiona para 1280x720 - 16:9)
      const workbookCoverUrl = await uploadWorkbookCover(file)
      return workbookCoverUrl
    },
  })
}

// ─── Company Logo Upload (super_admin only) ───────────────────────────────

export async function uploadCompanyLogo(file: File, companyId?: string): Promise<string> {
  const maxSize = 5 * 1024 * 1024
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use JPG, PNG, WEBP ou GIF')
  }
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  const token = localStorage.getItem('token')
  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload')
  }

  const formData = new FormData()
  formData.append('logo', file)
  if (companyId) formData.append('companyId', companyId)

  try {
    const uploadUrl = import.meta.env.VITE_UPLOAD_COMPANY_LOGO_URL || '/upload-company-logo.php'
    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }
    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error((error.response.data as UploadError).error || 'Erro ao fazer upload da logo')
    }
    throw error
  }
}

export function useUploadCompanyLogo() {
  return useMutation({
    mutationFn: async ({ file, companyId }: { file: File; companyId?: string }) => {
      return uploadCompanyLogo(file, companyId)
    },
  })
}

// ─── Badge Icon Upload (admin / super_admin) ──────────────────────────────

/**
 * Faz upload de um ícone PNG transparente pra badge.
 * PHP redimensiona pra 256×256 preservando alpha channel.
 *
 * Aceita SOMENTE PNG (não JPG/WebP) — badges precisam de fundo
 * transparente pra renderizar bem sobre a medalha colorida do tier.
 */
export async function uploadBadgeIcon(file: File): Promise<string> {
  const maxSize = 5 * 1024 * 1024 // 5MB
  // SOMENTE PNG (precisa de transparência)
  const allowedTypes = ['image/png']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use apenas PNG (precisa transparência).')
  }
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  const token = localStorage.getItem('token')
  if (!token) {
    throw new Error('Você precisa estar logado para fazer upload')
  }

  const formData = new FormData()
  formData.append('badgeIcon', file)

  try {
    const uploadUrl = import.meta.env.VITE_UPLOAD_BADGE_ICON_URL || '/upload-badge-icon.php'
    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }
    throw new Error(response.data.message || 'Erro ao fazer upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error((error.response.data as UploadError).error || 'Erro ao fazer upload do ícone')
    }
    throw error
  }
}

/**
 * Hook React Query pra upload de ícone de badge (super_admin/admin only).
 * Imagem é redimensionada server-side pra 256×256 preservando transparência.
 */
export function useUploadBadgeIcon() {
  return useMutation({
    mutationFn: async (file: File) => {
      return uploadBadgeIcon(file)
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Certificado — fundo (template) e assinatura do diretor
// admin OU super_admin podem fazer upload (não só super, diferente de logo)

/**
 * Upload do fundo do certificado (template PNG/JPG/WebP, máx 5MB).
 * PHP redimensiona pra 1240×877 (A4 paisagem ~150dpi). companyId
 * opcional — só é usado pra nome do arquivo (super_admin sem
 * companyId vira "global").
 */
export async function uploadCertificateBackground(file: File, companyId?: string): Promise<string> {
  const maxSize = 5 * 1024 * 1024
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo não permitido. Use JPG, PNG ou WebP')
  }
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  const token = localStorage.getItem('token')
  if (!token) throw new Error('Você precisa estar logado')

  const formData = new FormData()
  formData.append('certificateBackground', file)
  if (companyId) formData.append('companyId', companyId)

  try {
    const uploadUrl =
      import.meta.env.VITE_UPLOAD_CERTIFICATE_BACKGROUND_URL || '/upload-certificate-background.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) return response.data.url
    throw new Error(response.data.message || 'Erro no upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error((error.response.data as UploadError).error || 'Erro no upload do fundo')
    }
    throw error
  }
}

export function useUploadCertificateBackground() {
  return useMutation({
    mutationFn: async ({ file, companyId }: { file: File; companyId?: string }) =>
      uploadCertificateBackground(file, companyId),
  })
}

/**
 * Upload da assinatura do diretor. PNG transparente é o recomendado, mas
 * JPG/WebP também são aceitos. PHP redimensiona pra 600×200 e salva como PNG.
 */
export async function uploadCertificateSignature(file: File, companyId?: string): Promise<string> {
  // O tipo é validado server-side pelo PHP (finfo, pelo conteúdo real) — o
  // `file.type` do browser é pouco confiável e às vezes vem vazio num PNG
  // válido. Aqui validamos só o tamanho em bytes; as dimensões em pixels
  // ficam por conta do resize.
  const maxSize = 5 * 1024 * 1024 // 5MB
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  const token = localStorage.getItem('token')
  if (!token) throw new Error('Você precisa estar logado')

  const formData = new FormData()
  formData.append('certificateSignature', file)
  if (companyId) formData.append('companyId', companyId)

  try {
    const uploadUrl =
      import.meta.env.VITE_UPLOAD_CERTIFICATE_SIGNATURE_URL || '/upload-certificate-signature.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) return response.data.url
    throw new Error(response.data.message || 'Erro no upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error((error.response.data as UploadError).error || 'Erro no upload da assinatura')
    }
    throw error
  }
}

export function useUploadCertificateSignature() {
  return useMutation({
    mutationFn: async ({ file, companyId }: { file: File; companyId?: string }) =>
      uploadCertificateSignature(file, companyId),
  })
}

/**
 * Upload da logo do certificado — campo dedicado, separado do logo de
 * avatar da empresa. PHP redimensiona proporcionalmente (sem crop) pra
 * ~800px de largura, preservando a proporção.
 */
export async function uploadCertificateLogo(file: File, companyId?: string): Promise<string> {
  const maxSize = 5 * 1024 * 1024
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Tipo não permitido. Use JPG, PNG ou WebP')
  }
  if (file.size > maxSize) {
    throw new Error('Arquivo muito grande. Máximo: 5MB')
  }

  const token = localStorage.getItem('token')
  if (!token) throw new Error('Você precisa estar logado')

  const formData = new FormData()
  formData.append('certificateLogo', file)
  if (companyId) formData.append('companyId', companyId)

  try {
    const uploadUrl =
      import.meta.env.VITE_UPLOAD_CERTIFICATE_LOGO_URL || '/upload-certificate-logo.php'

    const response = await axios.post<UploadResponse>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success && response.data.url) return response.data.url
    throw new Error(response.data.message || 'Erro no upload')
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error((error.response.data as UploadError).error || 'Erro no upload da logo')
    }
    throw error
  }
}

export function useUploadCertificateLogo() {
  return useMutation({
    mutationFn: async ({ file, companyId }: { file: File; companyId?: string }) =>
      uploadCertificateLogo(file, companyId),
  })
}
